import { DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { FeatureRadio } from '@/components/chariot-ui';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateApiKey } from '@/hooks/useApiKey';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useWhoami } from '@/hooks/useWhoami';
import { ApiKey } from '@/types';
import { getFutureDateInISO } from '@/utils/date.util';
import { getFilteredRoleOptions } from '@/utils/role.util';

interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * ApiKeyModal - Modal for creating new API keys with expiration selection
 *
 * Features:
 * - Token name input with validation
 * - Expiration period selection (7d, 30d, 180d, 1y)
 * - Immediate modal close upon successful creation
 * - Success toast notification
 * - Table auto-updates via TanStack Query cache invalidation
 *
 * Workflow:
 * 1. User enters token name and selects expiration
 * 2. Click "Add Token"
 * 3. API key is created
 * 4. Success toast appears
 * 5. Modal closes immediately
 * 6. Table refreshes automatically with new key
 *
 * UX Improvement: Removed second step (ID/secret display) to eliminate
 * unnecessary "Done" button click. Users return to table immediately.
 *
 * Extracted from ApiKeys.tsx (Lines 206-377)
 * Used in: UserSettingsCards
 */
export function ApiKeyModal({ open, onClose }: ApiKeyModalProps) {
  const [tokenName, setTokenName] = useState('');
  const [expiration, setExpiration] = useState<string>('1y');
  const [createdKey, setCreatedKey] = useState<ApiKey | null>(null);
  const { mutateAsync: createApiKey, isPending } = useCreateApiKey();
  const rbacEnabled = useRbacEnabled();
  const { data: whoami } = useWhoami();
  const { isPraetorian } = useEntitlements();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const roleOptions = rbacEnabled
    ? getFilteredRoleOptions(whoami?.role, false, undefined, isPraetorian)
    : [];

  // Validation: token name must not be empty or whitespace-only
  const isTokenNameValid = tokenName.trim() !== '';
  const isRoleValid = !rbacEnabled || selectedRole !== '';

  const expirationOptions = useMemo(
    () => [
      { label: '7 Days', value: '7d', duration: { days: 7 } },
      { label: '30 Days', value: '30d', duration: { days: 30 } },
      {
        label: '180 Days',
        value: '180d',
        duration: { days: 180 },
      },
      { label: '1 Year', value: '1y', duration: { years: 1 } },
    ],
    []
  );

  const handleAddToken = async () => {
    try {
      const duration = expirationOptions.find(
        opt => opt.value === expiration
      )?.duration;
      const payload: { name: string; expires: string; role?: string } = {
        name: tokenName,
        expires: duration ? getFutureDateInISO(duration) : '',
        ...(rbacEnabled && selectedRole ? { role: selectedRole } : {}),
      };
      const result = await createApiKey(payload);

      // Show the created key with secret in success state
      setCreatedKey(result);
    } catch (e) {
      toast.error('Failed to create API key', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const handleClose = () => {
    setTokenName('');
    setExpiration('1y');
    setSelectedRole('');
    setCreatedKey(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableAutoFocus={!createdKey}
      title={
        <div className="text-2xl font-bold">
          {createdKey ? 'API Key Created' : 'Add Token'}
        </div>
      }
      divider={true}
      size={createdKey ? '4xl' : 'xl'}
      className="p-0"
      footer={
        createdKey
          ? {
              text: 'Done',
              onClick: handleClose,
              'data-testid': 'api-key-done-button',
            }
          : {
              text: isPending ? 'Creating...' : 'Add Token',
              onClick: handleAddToken,
              disabled: !isTokenNameValid || !isRoleValid || isPending,
              'data-testid': 'add-api-key-button',
            }
      }
      data-testid="api-key-modal"
    >
      <div className="flex flex-col gap-6 p-6">
        {!createdKey ? (
          // Form state - collect name and expiration
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Label label="Name" required={true} />
              <input
                id="token-name"
                type="text"
                className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:outline-hidden"
                placeholder="Enter token name"
                value={tokenName}
                onChange={e => setTokenName(e.target.value)}
                required
                autoFocus
                data-testid="api-key-name-input"
              />
            </div>
            <div className="border-chariot-stroke flex flex-col gap-1 rounded-md border p-2">
              <Label label="Set Expiration Period on Token" />
              <div className="mt-1 flex gap-2">
                {expirationOptions.map(opt => (
                  <FeatureRadio
                    key={opt.value}
                    id={`expiration-${opt.value}`}
                    title={opt.label}
                    checked={expiration === opt.value}
                    onChange={() => setExpiration(opt.value)}
                    className="w-fit flex-1 gap-2 rounded-none border px-2 py-1 [&>div]:mt-px"
                    description=""
                    data-testid={`api-key-expiration-radio-${opt.value}`}
                  />
                ))}
              </div>
            </div>
            {rbacEnabled && (
              <div className="border-chariot-stroke flex flex-col gap-1 rounded-md border p-2">
                <Label label="Role" required={true} />
                <Select
                  value={selectedRole || undefined}
                  onValueChange={setSelectedRole}
                >
                  <SelectTrigger data-testid="api-key-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : (
          // Success state - show API key ID and secret
          <ApiKeySuccessDisplay apiKey={createdKey} />
        )}
      </div>
    </Modal>
  );
}

/**
 * ApiKeySuccessDisplay - Shows the created API key with copy functionality
 *
 * Displays:
 * - Warning banner about copying the secret (only shown once)
 * - API Key ID with copy button
 * - API Key Secret with copy button
 *
 * Features:
 * - Copy to clipboard with visual feedback (green flash)
 * - Success toast notifications
 * - Monospace font for technical values
 */
function ApiKeySuccessDisplay({ apiKey }: { apiKey: ApiKey }) {
  const [copiedField, setCopiedField] = useState<'id' | 'secret' | null>(null);

  const handleCopy = async (value: string, field: 'id' | 'secret') => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(
        `API Key ${field === 'id' ? 'ID' : 'Secret'} copied to clipboard`
      );
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Warning Banner */}
      <div className="text-chariot-text-primary flex gap-3 rounded-md border border-yellow-500/20 bg-yellow-500/10 p-4">
        <div className="mt-0.5 shrink-0 text-yellow-600">
          <svg
            className="size-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-semibold">Copy your API key secret now</p>
          <p className="text-chariot-text-secondary text-sm">
            This is the only time the secret will be displayed. Store it
            securely as you won&apos;t be able to see it again.
          </p>
        </div>
      </div>

      {/* API Key ID */}
      <div className="flex flex-col gap-2">
        <label className="text-chariot-text-primary text-sm font-semibold">
          API Key ID
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => handleCopy(apiKey.id, 'id')}
            className={`text-chariot-text-primary min-w-0 flex-1 overflow-hidden rounded-md border px-3 py-2 text-left font-mono text-sm transition-colors ${
              copiedField === 'id'
                ? 'border-green-500/20 bg-green-500/10 text-green-600 ring-1 ring-green-500/20'
                : 'border-chariot-stroke bg-chariot-background-secondary hover:bg-chariot-background-tertiary'
            }`}
            data-testid="api-key-id-copy-button"
          >
            <span className="block overflow-x-auto whitespace-nowrap">
              {apiKey.id}
            </span>
          </button>
          <Button
            onClick={() => handleCopy(apiKey.id, 'id')}
            variant="outline"
            size="sm"
            className="shrink-0"
            data-testid="api-key-id-copy-icon-button"
          >
            <DocumentDuplicateIcon className="size-4" />
            Copy
          </Button>
        </div>
      </div>

      {/* API Key Secret */}
      <div className="flex flex-col gap-2">
        <label className="text-chariot-text-primary text-sm font-semibold">
          API Key Secret
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => handleCopy(apiKey.secret, 'secret')}
            className={`text-chariot-text-primary min-w-0 flex-1 overflow-hidden rounded-md border px-3 py-2 text-left font-mono text-sm transition-colors ${
              copiedField === 'secret'
                ? 'border-green-500/20 bg-green-500/10 text-green-600 ring-1 ring-green-500/20'
                : 'border-chariot-stroke bg-chariot-background-secondary hover:bg-chariot-background-tertiary'
            }`}
            data-testid="api-key-secret-copy-button"
          >
            <span className="block overflow-x-auto whitespace-nowrap">
              {apiKey.secret}
            </span>
          </button>
          <Button
            onClick={() => handleCopy(apiKey.secret, 'secret')}
            variant="outline"
            size="sm"
            className="shrink-0"
            data-testid="api-key-secret-copy-icon-button"
          >
            <DocumentDuplicateIcon className="size-4" />
            Copy
          </Button>
        </div>
      </div>

      {/* Helper Text */}
      <p className="text-chariot-text-secondary text-sm">
        Use this API key to authenticate with the Chariot API. Include the ID
        and secret in your API requests as specified in the{' '}
        <a
          href="https://docs.praetorian.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-chariot-brand-primary hover:underline"
        >
          API documentation
        </a>
        .
      </p>
    </div>
  );
}

/**
 * Label component for form fields
 * Internal helper for ApiKeyModal form labels
 */
function Label({ label, required }: { label: string; required?: boolean }) {
  return (
    <label
      className="text-chariot-text-primary text-sm font-semibold"
      htmlFor="token-expiration"
    >
      <span>{label}</span>
      {required && <span className="text-chariot-status-critical">*</span>}
    </label>
  );
}
